/**
 * Session state.
 *
 * Kept in React state rather than read from storage on every render so a token
 * refresh re-renders consumers exactly once. `sessionStorage` remains the source
 * of truth across reloads; this only mirrors it for the current tab.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { ApiClient, clearSession, loadSession, type Session } from '../lib/api-client';
import type { LoginInput } from '@retailpro/shared';

interface SessionState {
  session: Session | null;
  api: ApiClient;
  signIn: (input: LoginInput) => Promise<Session>;
  signOut: () => Promise<void>;
}

const SessionContext = createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(() => loadSession());

  // One client for the tab. Losing the session is a state transition, not a
  // reload, so a user typing into a form mid-request is not bounced sideways.
  const api = useMemo(() => new ApiClient(undefined, () => setSession(null)), []);

  useEffect(() => {
    if (session) return;
    clearSession();
  }, [session]);

  const signIn = useCallback(
    async (input: LoginInput) => {
      const next = await api.login(input);
      setSession(next);
      return next;
    },
    [api],
  );

  const signOut = useCallback(async () => {
    const current = loadSession();
    if (current) await api.logout(current);
    setSession(null);
  }, [api]);

  const value = useMemo<SessionState>(
    () => ({ session, api, signIn, signOut }),
    [session, api, signIn, signOut],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionState {
  const value = useContext(SessionContext);
  if (!value) throw new Error('useSession must be used inside <SessionProvider>.');
  return value;
}
